import React, { useState, useEffect } from 'react';
import {
    Modal,
    Upload,
    Button,
    message,
    Image,
    Space,
    Typography,
    Card,
    Row,
    Col,
    Popconfirm,
    List,
    Progress
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    UploadOutlined,
    LoadingOutlined,
    EyeOutlined,
    InboxOutlined,
    PictureOutlined
} from '@ant-design/icons';
import auctionService from '../../services/auctionService';
import env from '../../env';
import { useParams } from 'react-router-dom';
const { Title, Text } = Typography;

const { Dragger } = Upload;

const imageStyle = {
    width: '100%',
    height: '200px',
    objectFit: 'cover',
    objectPosition: 'center',
    borderRadius: '8px 8px 0 0',
    transition: 'transform 0.3s ease'
};

const cardStyle = {
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    transition: 'all 0.3s ease',
    background: '#ffffff',
    border: '1px solid #f0f0f0',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)'
    }
};

const sectionStyle = {
    background: 'linear-gradient(to bottom, #ffffff, #fafafa)',
    padding: '32px',
    borderRadius: '16px',
    marginTop: '32px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    border: '1px solid #f0f0f0'
};

const draggerStyle = {
    borderRadius: '12px',
    border: '2px dashed #d9d9d9',
    background: '#fafafa',
    transition: 'all 0.3s ease',
    '&:hover': {
        borderColor: '#1890ff',
        background: '#f0f7ff'
    }
};

const ImageUpload = ({ 
    entityType, 
    entityId, 
    entitySlug,
    onSuccess, 
    showDragger = false,
    disabled = false
}) => {
    const [fileList, setFileList] = useState([]);
    const [pendingUploads, setPendingUploads] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [deletingImageId, setDeletingImageId] = useState(null);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const { auctionSlug: urlAuctionSlug, lotSlug: urlLotSlug } = useParams();

    // Use URL params if available, otherwise use props
    const auctionSlug = urlAuctionSlug || entitySlug;
    const lotSlug = urlLotSlug;

    useEffect(() => {
        if (auctionSlug) {
            loadImages();
        }
    }, [entityType, auctionSlug, lotSlug]);

    const loadImages = async () => {
        try {
            let response;
            if (entityType === 'auction') {
                response = await auctionService.getAuctionImages(auctionSlug);
                console.log('Auction images response:', response);
                
                if (response.data && response.data.data && Array.isArray(response.data.data.images)) {
                    console.log('Found images:', response.data.data.images);
                    setFileList(response.data.data.images.map(image => ({
                        uid: image.id,
                        name: image.path.split('/').pop(),
                        status: 'done',
                        url: image.path,
                        path: image.path
                    })));
                } else {
                    console.log('No images found in response:', response.data);
                    setFileList([]);
                }
            } else if (entityType === 'lot') {
                response = await auctionService.getLotImages(auctionSlug, lotSlug);
                console.log('Lot images response:', response);
                
                if (response.data && response.data.data && Array.isArray(response.data.data.images)) {
                    console.log('Found images:', response.data.data.images);
                    setFileList(response.data.data.images.map(image => ({
                        uid: image.id,
                        name: image.path.split('/').pop(),
                        status: 'done',
                        url: image.path,
                        path: image.path
                    })));
                } else {
                    console.log('No images found in response:', response.data);
                    setFileList([]);
                }
            }
        } catch (error) {
            console.error('Error loading images:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            message.error('Failed to load images');
            setFileList([]);
        }
    };

    const handleFileSelect = (file) => {
        if (disabled) {
            message.error('Cannot upload images: The auction is already completed');
            return Upload.LIST_IGNORE;
        }

        const isImage = file.type.startsWith('image/');
        if (!isImage) {
            message.error('You can only upload image files!');
            return Upload.LIST_IGNORE;
        }
        const isLt10M = file.size / 1024 / 1024 < 10;
        if (!isLt10M) {
            message.error('Image must be smaller than 10MB!');
            return Upload.LIST_IGNORE;
        }

        // Add to pending uploads
        setPendingUploads(prev => [...prev, file]);
        return false; // Prevent default upload behavior
    };

    const handleConfirmUpload = async () => {
        if (disabled) {
            message.error('Cannot upload images: The auction is already completed');
            return;
        }

        if (pendingUploads.length === 0) {
            message.warning('No images to upload');
            return;
        }

        if (!auctionSlug) {
            message.error('Auction slug is required');
            return;
        }

        setUploading(true);
        setUploadProgress(0);
        const formData = new FormData();
        pendingUploads.forEach(file => {
            formData.append('images[]', file);
        });

        try {
            console.log('Uploading files:', pendingUploads);
            console.log('Entity Type:', entityType);
            console.log('Auction Slug:', auctionSlug);
            console.log('Lot Slug:', lotSlug);

            let response;
            if (entityType === 'auction') {
                response = await auctionService.uploadAuctionImages(
                    auctionSlug, 
                    formData,
                    (progressEvent) => {
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        setUploadProgress(percentCompleted);
                    }
                );
            } else if (entityType === 'lot') {
                if (!lotSlug) {
                    message.error('Lot slug is required');
                    return;
                }
                response = await auctionService.uploadLotImages(
                    auctionSlug, 
                    lotSlug, 
                    formData,
                    (progressEvent) => {
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        setUploadProgress(percentCompleted);
                    }
                );
            }

            console.log('Upload response:', response);

            if (response.data) {
                setUploadProgress(100);
                message.success('Images uploaded successfully');
                setPendingUploads([]); // Clear pending uploads
                loadImages(); // Reload images after successful upload
                if (onSuccess) onSuccess();
                // Reset progress after a short delay
                setTimeout(() => setUploadProgress(0), 1000);
            }
        } catch (error) {
            console.error('Error uploading images:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            setUploadProgress(0);
            message.error(error.response?.data?.message || 'Failed to upload images');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (file) => {
        if (disabled) {
            message.error('Cannot delete images: The auction is already completed');
            return;
        }

        if (!auctionSlug) {
            message.error('Auction slug is required');
            return;
        }

        setDeletingImageId(file.uid);
        try {
            if (entityType === 'auction') {
                await auctionService.deleteAuctionImage(auctionSlug, file.uid);
            } else if (entityType === 'lot') {
                if (!lotSlug) {
                    message.error('Lot slug is required');
                    return;
                }
                await auctionService.deleteLotImage(auctionSlug, lotSlug, file.uid);
            }
            message.success('Image deleted successfully');
            loadImages(); // Reload images after successful deletion
        } catch (error) {
            console.error('Error deleting image:', error);
            message.error('Failed to delete image');
        } finally {
            setDeletingImageId(null);
        }
    };

    const handlePreview = (file) => {
        console.log('Previewing file:', file);
        setPreviewImage(`${env.BACKEND_STORAGE_URL}/${file.url}`);
        setPreviewVisible(true);
    };

    const handleRemovePending = (file) => {
        setPendingUploads(prev => prev.filter(f => f !== file));
    };

    const uploadProps = {
        name: 'images',
        multiple: true,
        showUploadList: false,
        beforeUpload: handleFileSelect,
        accept: 'image/*',
        disabled: disabled
    };

    return (
        <div>
            {showDragger ? (
                <Dragger {...uploadProps} style={{ borderRadius: '8px' }}>
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                    </p>
                    <p className="ant-upload-text" style={{ fontSize: '16px', marginBottom: '8px' }}>
                        Click or drag files to this area to upload
                    </p>
                    <p className="ant-upload-hint" style={{ color: '#666' }}>
                        Support for multiple image uploads. Strictly prohibited from uploading company data or other
                        banned files.
                    </p>
                    {entityType === 'auction' && (
                        <div style={{ 
                            marginTop: '16px',
                            padding: '8px 16px',
                            background: 'rgba(24, 144, 255, 0.1)',
                            borderRadius: '4px',
                            display: 'inline-block'
                        }}>
                            <Space>
                                <PictureOutlined style={{ color: '#1890ff' }} />
                                <span style={{ color: '#1890ff', fontWeight: '500' }}>
                                    Uploading images for auction: {auctionSlug}
                                </span>
                            </Space>
                        </div>
                    )}
                    {entityType === 'lot' && (
                        <div style={{ 
                            marginTop: '16px',
                            padding: '8px 16px',
                            background: 'rgba(24, 144, 255, 0.1)',
                            borderRadius: '4px',
                            display: 'inline-block'
                        }}>
                            <Space>
                                <PictureOutlined style={{ color: '#1890ff' }} />
                                <span style={{ color: '#1890ff', fontWeight: '500' }}>
                                    Uploading images for lot: {entitySlug}
                                </span>
                            </Space>
                        </div>
                    )}
                </Dragger>
            ) : (
                <Upload {...uploadProps}>
                    <Button icon={<UploadOutlined />}>Select Images</Button>
                </Upload>
            )}

            {pendingUploads.length > 0 && (
                <div style={sectionStyle}>
                    <div style={{ 
                        display: 'flex', 
                        flexDirection: window.innerWidth < 576 ? 'column' : 'row',
                        justifyContent: 'space-between', 
                        alignItems: window.innerWidth < 576 ? 'stretch' : 'center', 
                        marginBottom: '32px',
                        padding: '0 8px',
                        gap: window.innerWidth < 576 ? '16px' : '0'
                    }}>
                        <Title level={4} style={{ 
                            margin: 0,
                            color: '#262626',
                            fontWeight: '600',
                            fontSize: window.innerWidth < 576 ? '16px' : '18px'
                        }}>
                            Images to Upload
                        </Title>
                        <Button
                            type="primary"
                            onClick={handleConfirmUpload}
                            loading={uploading}
                            disabled={disabled}
                            size={window.innerWidth < 576 ? 'default' : 'large'}
                            block={window.innerWidth < 576}
                            style={{
                                height: window.innerWidth < 576 ? '40px' : '48px',
                                padding: window.innerWidth < 576 ? '0 16px' : '0 24px',
                                borderRadius: '8px',
                                background: 'linear-gradient(45deg, #1890ff, #40a9ff)',
                                border: 'none',
                                boxShadow: '0 2px 8px rgba(24,144,255,0.2)'
                            }}
                        >
                            {uploading ? 'Uploading...' : 'Confirm Upload'}
                        </Button>
                    </div>
                    {uploading && (
                        <div style={{ 
                            marginBottom: '24px',
                            padding: '0 8px'
                        }}>
                            <Progress 
                                percent={uploadProgress} 
                                status={uploadProgress === 100 ? 'success' : 'active'}
                                strokeColor={{
                                    '0%': '#108ee9',
                                    '100%': '#87d068',
                                }}
                                showInfo={true}
                                format={(percent) => `${percent}%`}
                            />
                            <Text type="secondary" style={{ 
                                display: 'block', 
                                marginTop: '8px',
                                fontSize: '12px',
                                textAlign: 'center'
                            }}>
                                Uploading {pendingUploads.length} image{pendingUploads.length > 1 ? 's' : ''}...
                            </Text>
                        </div>
                    )}
                    <List
                        grid={{ 
                            gutter: [16, 16], 
                            xs: 1,
                            sm: 2,
                            md: 3,
                            lg: 4,
                            xl: 4,
                            xxl: 4
                        }}
                        dataSource={pendingUploads}
                        renderItem={item => (
                            <List.Item>
                                <Card
                                    hoverable
                                    style={cardStyle}
                                    cover={
                                        <div style={{ 
                                            height: '200px', 
                                            overflow: 'hidden',
                                            '&:hover img': {
                                                transform: 'scale(1.05)'
                                            }
                                        }}>
                                            <img 
                                                alt={item.name} 
                                                src={URL.createObjectURL(item)}
                                                style={imageStyle}
                                            />
                                        </div>
                                    }
                                    actions={[
                                        <Button 
                                            type="text" 
                                            danger 
                                            icon={<DeleteOutlined />}
                                            onClick={() => handleRemovePending(item)}
                                            style={{ color: '#ff4d4f' }}
                                        >
                                            Remove
                                        </Button>
                                    ]}
                                >
                                    <Card.Meta 
                                        title={item.name} 
                                        description={`${(item.size / 1024 / 1024).toFixed(2)} MB`}
                                        style={{
                                            '& .ant-card-meta-title': {
                                                color: '#262626',
                                                fontWeight: '500'
                                            },
                                            '& .ant-card-meta-description': {
                                                color: '#8c8c8c'
                                            }
                                        }}
                                    />
                                </Card>
                            </List.Item>
                        )}
                    />
                </div>
            )}

            {fileList.length > 0 && (
                <div style={sectionStyle}>
                    <Title level={4} style={{ 
                        marginBottom: '32px', 
                        padding: '0 8px',
                        color: '#262626',
                        fontWeight: '600',
                        fontSize: window.innerWidth < 576 ? '16px' : '18px'
                    }}>
                        Existing Images
                    </Title>
                    <List
                        loading={uploading}
                        grid={{ 
                            gutter: [16, 16], 
                            xs: 1,
                            sm: 2,
                            md: 3,
                            lg: 4,
                            xl: 4,
                            xxl: 4
                        }}
                        dataSource={fileList}
                        renderItem={item => (
                            <List.Item>
                                <Card
                                    hoverable
                                    style={cardStyle}
                                    cover={
                                        <div style={{ 
                                            height: '200px', 
                                            overflow: 'hidden',
                                            '&:hover img': {
                                                transform: 'scale(1.05)'
                                            }
                                        }}>
                                            <img 
                                                alt={item.name} 
                                                src={`${env.BACKEND_STORAGE_URL}/${item.url}`}
                                                style={imageStyle}
                                            />
                                        </div>
                                    }
                                    actions={[
                                        <Button 
                                            type="text" 
                                            icon={<EyeOutlined />}
                                            onClick={() => handlePreview(item)}
                                            style={{ color: '#1890ff' }}
                                        >
                                            Preview
                                        </Button>,
                                        <Popconfirm
                                            title="Delete this image?"
                                            description="Are you sure you want to delete this image?"
                                            onConfirm={() => handleDelete(item)}
                                            okText="Yes"
                                            cancelText="No"
                                            disabled={disabled}
                                        >
                                            <Button 
                                                type="text" 
                                                danger 
                                                icon={deletingImageId === item.uid ? <LoadingOutlined /> : <DeleteOutlined />}
                                                loading={deletingImageId === item.uid}
                                                disabled={disabled}
                                                style={{ color: '#ff4d4f' }}
                                            >
                                                Delete
                                            </Button>
                                        </Popconfirm>
                                    ]}
                                >
                                    <Card.Meta 
                                        title={item.name}
                                        description="Uploaded image"
                                        style={{
                                            '& .ant-card-meta-title': {
                                                color: '#262626',
                                                fontWeight: '500'
                                            },
                                            '& .ant-card-meta-description': {
                                                color: '#8c8c8c'
                                            }
                                        }}
                                    />
                                </Card>
                            </List.Item>
                        )}
                    />
                </div>
            )}

            <Modal
                title="Image Preview"
                open={previewVisible}
                onCancel={() => setPreviewVisible(false)}
                footer={null}
                width={window.innerWidth < 768 ? '95%' : 800}
                centered
                style={{
                    '& .ant-modal-content': {
                        borderRadius: '16px',
                        overflow: 'hidden'
                    }
                }}
            >
                <img 
                    alt="preview" 
                    style={{ 
                        width: '100%', 
                        maxHeight: '80vh',
                        objectFit: 'contain',
                        borderRadius: '8px'
                    }} 
                    src={previewImage} 
                />
            </Modal>
        </div>
    );
};

export default ImageUpload; 